import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { dilapidationService } from "@/api/services/dilapidation.service";
import { jobsService } from "@/api/services/jobs.service";
import { documentsService, } from "@/api/services/documents.service";
import { jobToUpdateDto } from "@/utils/jobUtils";
import { parseDateTime, toDateTime, toDateOnly, formatDateOnlyDisplay, formatDateOnlyAUS, } from "@/utils/dateTime";
import { DateInput } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useAlert } from "@/hooks/useAlert";
import { ChangeDateModal } from "@/features/admin/jobs/components/ChangeDateModal";
import { UploadDocumentsModal } from "./components/UploadDocumentsModal";
import { formatJobAddress } from "@/utils/address";
import styles from "./ClientJobDetail.module.css";
const BackIcon = () => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("polyline", { points: "15 18 9 12 15 6" }) }));
const CalendarIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }));
const ClockIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("polyline", { points: "12 6 12 12 16 14" })] }));
const DownloadIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] }));
const UploadIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "17 8 12 3 7 8" }), _jsx("line", { x1: "12", y1: "3", x2: "12", y2: "15" })] }));
const formatDate = (dateString) => formatDateOnlyDisplay(dateString ?? undefined);
const formatTime = (timeString) => {
    if (!timeString)
        return "12 : 00 M";
    return timeString;
};
const formatDocDate = (dateString) => formatDateOnlyAUS(dateString);
const formatFileSize = (bytes) => {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
export const ClientJobDetail = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { alert } = useAlert();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Date change modal state
    const [showDateChangeModal, setShowDateChangeModal] = useState(false);
    const [dateChangeSubmitting, setDateChangeSubmitting] = useState(false);
    const [newCommencementDate, setNewCommencementDate] = useState("");
    const [newCompletionDate, setNewCompletionDate] = useState("");
    const [newDuration, setNewDuration] = useState("");
    const [dateChangeReason, setDateChangeReason] = useState("");
    const [isDateChangeModalOpen, setIsDateChangeModalOpen] = useState(false);
    const [hasDilapidation, setHasDilapidation] = useState(false);
    // Fetch job details
    const fetchJob = useCallback(async () => {
        if (!jobId)
            return;
        try {
            setLoading(true);
            setError(null);
            const response = await jobsService.getJob(jobId);
            setJob(response.data);
        }
        catch (err) {
            console.error("Error fetching job:", err);
            setError("Failed to load job details. Please try again.");
        }
        finally {
            setLoading(false);
        }
    }, [jobId]);
    useEffect(() => {
        fetchJob();
    }, [fetchJob]);
    const fetchDilapidationExists = useCallback(async () => {
        if (!jobId) {
            setHasDilapidation(false);
            return;
        }
        try {
            const res = await dilapidationService.getByJobId(jobId);
            // Handle: { dilapidation }, { data }, or dilapidation at root
            const d = res.data?.dilapidation ??
                res.data?.data ??
                (res.data && typeof res.data === "object" && "jobId" in res.data
                    ? res.data
                    : null);
            const dilapidation = d && typeof d === "object" && "status" in d ? d : null;
            const status = dilapidation?.status;
            setHasDilapidation(!!dilapidation && status === "Public");
        }
        catch {
            setHasDilapidation(false);
        }
    }, [jobId]);
    useEffect(() => {
        fetchDilapidationExists();
    }, [fetchDilapidationExists]);
    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible" && jobId) {
                fetchDilapidationExists();
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, [jobId, fetchDilapidationExists]);
    const handleBack = () => {
        navigate("/client/jobs");
    };
    const handleRequestDateChange = () => {
        if (job) {
            const { date: commDate } = parseDateTime(job.commencementDate || job.scheduledDate);
            const { date: compDate } = parseDateTime(job.completionDate);
            setNewCommencementDate(commDate);
            setNewCompletionDate(compDate);
            setNewDuration(job.durationDays?.toString() || "");
            setDateChangeReason("");
        }
        setShowDateChangeModal(true);
    };
    const handleSubmitDateChange = async () => {
        if (!jobId || !job)
            return;
        try {
            setDateChangeSubmitting(true);
            const commDate = toDateOnly(newCommencementDate) ||
                parseDateTime(job.commencementDate || job.scheduledDate).date;
            const compDate = toDateOnly(newCompletionDate) || parseDateTime(job.completionDate).date;
            const { time: commTime } = parseDateTime(job.commencementDate || job.scheduledDate);
            const { time: compTime } = parseDateTime(job.completionDate);
            const commencementDateTime = toDateTime(commDate, commTime || "00:00");
            const completionDateTime = toDateTime(compDate, compTime || "00:00");
            const duration = newDuration
                ? parseInt(newDuration)
                : (job.durationDays ?? undefined);
            // 1. Apply dates directly to job (no approval needed) – full body for PUT api/jobs/{id}
            const updateDto = jobToUpdateDto(job, {
                commencementDate: commencementDateTime || undefined,
                completionDate: completionDateTime || undefined,
                durationDays: duration,
            });
            await jobsService.updateJob(jobId, updateDto);
            // 2. Create date change request for notification (admin sees Action Required)
            const dto = {
                jobId,
                requestor: "Client",
                requestedById: user?.id ?? "",
                requestedByName: user?.name ?? "",
                previousDuration: job.durationDays ?? undefined,
                previousCommencementDate: job.commencementDate || job.scheduledDate || undefined,
                previousCompletionDate: job.completionDate ?? undefined,
                newCommencementDate: commencementDateTime || undefined,
                newCompletionDate: completionDateTime || undefined,
                newDuration: duration,
                reason: dateChangeReason || undefined,
            };
            await jobsService.createDateChangeRequest(dto);
            setShowDateChangeModal(false);
            await fetchJob();
            await alert("Date change applied successfully! Staff will be notified.");
        }
        catch (err) {
            console.error("Error submitting date change:", err);
            await alert("Failed to submit date change. Please try again.");
        }
        finally {
            setDateChangeSubmitting(false);
        }
    };
    const pendingDateChangeRequest = job?.dateChangeRequests?.find((r) => r.status === "pending");
    const handleViewDetails = () => {
        setIsDateChangeModalOpen(true);
    };
    const handleCloseDateChangeModal = () => {
        setIsDateChangeModalOpen(false);
    };
    const handleApproveDateChange = async () => {
        if (!pendingDateChangeRequest)
            return;
        try {
            await jobsService.processDateChangeRequest(pendingDateChangeRequest.id, {
                action: "approved",
                approvedById: user?.id ?? "",
                approvedByName: user?.name ?? "",
            });
            await fetchJob();
            await alert("Date change approved! Dates have been updated.");
        }
        catch (err) {
            console.error("Failed to approve date change:", err);
            await alert("Failed to approve date change.");
        }
        setIsDateChangeModalOpen(false);
    };
    const handleCommunicateWithTDG = () => {
        handleCloseDateChangeModal();
        if (job) {
            navigate(`/client/notifications?jobId=${job.id}`);
        }
    };
    if (loading) {
        return (_jsx("div", { className: styles.page, children: _jsx("div", { className: styles.loadingState, children: _jsx("p", { children: "Loading job details..." }) }) }));
    }
    if (error || !job) {
        return (_jsx("div", { className: styles.page, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error || "Job not found" }), _jsx("button", { onClick: handleBack, className: styles.backBtn, children: "Back to Jobs" })] }) }));
    }
    return (_jsxs("div", { className: styles.page, children: [_jsxs("header", { className: styles.header, children: [_jsxs("button", { className: styles.backLink, onClick: handleBack, children: [_jsx(BackIcon, {}), _jsx("span", { children: "BACK" })] }), _jsx("h1", { className: styles.title, children: "View Job" })] }), jobId && hasDilapidation && (_jsx("section", { children: _jsx(Link, { to: `/client/jobs/${jobId}/dilapidation`, className: styles.dilapidationLink, children: "View Dilapidation Report" }) })), _jsx("div", { className: styles.jobCard, children: _jsxs("div", { className: styles.jobCardContent, children: [_jsxs("div", { className: styles.leftColumn, children: [_jsxs("h2", { className: styles.jobNumber, children: ["Job #", job.jobNumber] }), _jsxs("div", { className: styles.formRow, children: [_jsx("label", { className: styles.formLabel, children: "Job Name" }), _jsx("input", { type: "text", className: styles.formInput, value: job.title, readOnly: true })] }), _jsxs("div", { className: styles.formRow, children: [_jsx("label", { className: styles.formLabel, children: "Site Address" }), _jsx("input", { type: "text", className: styles.formInput, value: formatJobAddress(job) || "", readOnly: true })] }), _jsxs("div", { className: styles.formRow, children: [_jsx("label", { className: styles.formLabel, children: "Type" }), _jsxs("div", { className: styles.typeToggle, children: [_jsx("span", { className: `${styles.typeOption} ${job.type === "full_defit" ? styles.typeOptionActive : ""}`, children: "Full Defit" }), _jsx("div", { className: `${styles.toggleSwitch} ${job.type === "partial_defit" ? styles.toggleSwitchRight : ""}`, children: _jsx("div", { className: styles.toggleKnob }) }), _jsx("span", { className: `${styles.typeOption} ${job.type === "partial_defit" ? styles.typeOptionActive : ""}`, children: "Partial Defit" })] })] }), _jsxs("div", { className: styles.scopeSection, children: [_jsx("h3", { className: styles.scopeTitle, children: "JOB SCOPE" }), _jsx("p", { className: styles.scopeText, children: job.scope ||
                                                job.description ||
                                                "No scope description provided." })] })] }), _jsxs("div", { className: styles.rightColumn, children: [pendingDateChangeRequest?.requestor === "Staff" && (_jsxs("div", { className: styles.actionAlert, children: [_jsxs("div", { className: styles.alertContent, children: [_jsx("div", { className: styles.alertIcon, children: _jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" }), _jsx("line", { x1: "12", y1: "9", x2: "12", y2: "13" }), _jsx("line", { x1: "12", y1: "17", x2: "12.01", y2: "17" })] }) }), _jsxs("div", { className: styles.alertText, children: [_jsx("h3", { className: styles.alertTitle, children: "Action Required" }), _jsx("p", { className: styles.alertMessage, children: "The Defit Group needs to change the date of this Job" })] })] }), _jsx("button", { className: styles.viewDetailsBtn, onClick: handleViewDetails, type: "button", children: "View Details" })] })), _jsxs("div", { className: styles.dateRow, children: [_jsxs("label", { className: styles.dateLabel, children: ["Commencement", _jsx("br", {}), "Date and Time"] }), _jsxs("div", { className: styles.dateInputGroup, children: [_jsxs("div", { className: styles.dateInput, children: [_jsx("span", { children: formatDate(job.commencementDate || job.scheduledDate) }), _jsx(CalendarIcon, {})] }), _jsxs("div", { className: styles.timeInput, children: [_jsx("span", { children: formatTime(job.startTime) }), _jsx(ClockIcon, {})] })] })] }), _jsxs("div", { className: styles.dateRow, children: [_jsxs("label", { className: styles.dateLabel, children: ["Completion", _jsx("br", {}), "Date and Time"] }), _jsxs("div", { className: styles.dateInputGroup, children: [_jsxs("div", { className: styles.dateInput, children: [_jsx("span", { children: formatDate(job.completionDate) }), _jsx(CalendarIcon, {})] }), _jsxs("div", { className: styles.timeInput, children: [_jsx("span", { children: formatTime(job.endTime) }), _jsx(ClockIcon, {})] })] })] }), _jsxs("div", { className: styles.dateRow, children: [_jsx("label", { className: styles.dateLabel, children: "Project Duration (days)" }), _jsx("div", { className: styles.durationInput, children: _jsx("span", { children: job.durationDays || "TBD" }) })] }), _jsx("button", { className: styles.requestDateBtn, onClick: handleRequestDateChange, disabled: job.hasPendingDateChangeRequest, children: job.hasPendingDateChangeRequest
                                        ? "Date Change Pending"
                                        : "Request Job Date Change" })] })] }) }), _jsxs("section", { className: styles.documentsSection, children: [_jsx("h2", { className: styles.documentsSectionTitle, children: "Documents and Photos" }), _jsx(DocumentsTab, { job: job })] }), _jsx(ChangeDateModal, { isOpen: isDateChangeModalOpen, onClose: handleCloseDateChangeModal, onCommunicate: handleCommunicateWithTDG, communicateButtonLabel: "Communicate with TDG Staff", onApprove: handleApproveDateChange, dateChangeRequest: pendingDateChangeRequest, hideDecline: true, formatDatesAsLocal: false }), showDateChangeModal && (_jsx("div", { className: styles.modalOverlay, children: _jsxs("div", { className: styles.modal, children: [_jsx("h2", { className: styles.modalTitle, children: "Request Date Change" }), _jsxs("div", { className: styles.modalField, children: [_jsx("label", { children: "New Commencement Date" }), _jsx(DateInput, { value: newCommencementDate, onChange: setNewCommencementDate })] }), _jsxs("div", { className: styles.modalField, children: [_jsx("label", { children: "New Completion Date" }), _jsx(DateInput, { value: newCompletionDate, onChange: setNewCompletionDate })] }), _jsxs("div", { className: styles.modalField, children: [_jsx("label", { children: "New Duration (days)" }), _jsx("input", { type: "number", value: newDuration, onChange: (e) => setNewDuration(e.target.value) })] }), _jsxs("div", { className: styles.modalField, children: [_jsx("label", { children: "Reason for Change" }), _jsx("textarea", { value: dateChangeReason, onChange: (e) => setDateChangeReason(e.target.value), rows: 4 })] }), _jsxs("div", { className: styles.modalActions, children: [_jsx("button", { className: styles.modalCancelBtn, onClick: () => setShowDateChangeModal(false), disabled: dateChangeSubmitting, children: "Cancel" }), _jsx("button", { className: styles.modalSubmitBtn, onClick: handleSubmitDateChange, disabled: dateChangeSubmitting, children: dateChangeSubmitting ? "Submitting..." : "Submit Request" })] })] }) }))] }));
};
// Map job documentType to same category keys used for site doc filtering
const jobDocTypeToCategory = (documentType) => {
    switch (documentType) {
        case "building_permit":
        case "cfi_certificate":
        case "site_map":
        case "notice_to_vacate":
            return "job";
        case "plans":
            return "quote";
        case "invoices":
            return "invoice";
        case "images":
            return "images";
        case "additional_works":
            return "additional_works";
        case "other":
        default:
            return "other";
    }
};
const JOB_DOCUMENT_CATEGORIES = [
    "Permits",
    "Plans",
    "Invoices",
    "Images",
    "Additional Works",
    "Other",
];
// Map UI category to jobCategory (aligns with JobDocumentsPhotosTab)
const UI_CATEGORY_TO_JOB_CATEGORY = {
    permits: "permits",
    plans: "plans",
    invoices: "invoices",
    images: "images",
    additional_works: "additional_works",
    other: "other",
};
const DocumentsTab = ({ job }) => {
    const { alert } = useAlert();
    const [jobDocuments, setJobDocuments] = useState([]);
    const [jobDocumentsFromDocumentsApi, setJobDocumentsFromDocumentsApi] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const filters = [
        "All",
        "Permits",
        "Plans",
        "Invoices",
        "Images",
        "Additional Works",
        "Other",
    ];
    const fetchJobDocuments = useCallback(async () => {
        if (!job.id) {
            setJobDocuments([]);
            setJobDocumentsFromDocumentsApi([]);
            return;
        }
        try {
            const [jobsResponse, docsResponse] = await Promise.all([
                jobsService.getJobDocuments(job.id),
                documentsService.getDocumentsByJob(job.id),
            ]);
            setJobDocuments(jobsResponse.data);
            setJobDocumentsFromDocumentsApi(docsResponse.data);
        }
        catch (err) {
            console.error("Error fetching job documents:", err);
            setJobDocuments([]);
            setJobDocumentsFromDocumentsApi([]);
        }
    }, [job.id]);
    useEffect(() => {
        setLoading(true);
        fetchJobDocuments().finally(() => setLoading(false));
    }, [fetchJobDocuments]);
    const handleDownload = async (row) => {
        try {
            const response = row.jobApiSource === "documents"
                ? await documentsService.getDownloadUrl(row.id)
                : await jobsService.getJobDocumentDownloadUrl(row.id);
            window.open(response.data.url, "_blank");
        }
        catch (err) {
            console.error("Failed to get download URL:", err);
            await alert("Failed to download document. Please try again.");
        }
    };
    const handleSaveDocuments = async (uploadedFiles, category) => {
        if (!job.id)
            return;
        const key = (category ?? "").trim().toLowerCase().replace(/\s+/g, "_");
        const jobCategory = UI_CATEGORY_TO_JOB_CATEGORY[key] ?? "other";
        setIsUploading(true);
        try {
            for (const uploadedFile of uploadedFiles) {
                if (!uploadedFile.file) {
                    throw new Error(`File object missing for ${uploadedFile.name}`);
                }
                await documentsService.uploadJobDocument(uploadedFile.file, {
                    jobId: job.id,
                    name: uploadedFile.name,
                    jobCategory,
                });
            }
            await fetchJobDocuments();
            setIsUploadModalOpen(false);
            await alert("Documents uploaded successfully!");
        }
        catch (err) {
            console.error("Error uploading documents:", err);
            await alert("Failed to upload documents. Please try again.");
            throw err; // Rethrow so modal stays open with selection preserved
        }
        finally {
            setIsUploading(false);
        }
    };
    const categoryMap = {
        permits: ["contract", "job", "permits"],
        plans: ["quote", "plans"],
        invoices: ["invoice", "invoices"],
        images: ["images"],
        "additional works": ["additional_works"],
        other: ["invoice", "other"],
    };
    const combinedRows = [
        ...jobDocuments.map((doc) => ({
            id: doc.id,
            name: doc.name || doc.fileName,
            fileSize: doc.fileSize,
            mimeType: doc.mimeType,
            createdAt: doc.createdAt,
            uploadedByName: doc.uploadedByName ?? null,
            source: "job",
            category: jobDocTypeToCategory(doc.documentType),
            jobApiSource: "jobs",
        })),
        ...jobDocumentsFromDocumentsApi.map((doc) => {
            const rawCategory = doc.jobCategory ?? doc.siteCategory ?? doc.category ?? "other";
            const category = String(rawCategory).trim().toLowerCase();
            return {
                id: doc.id,
                name: doc.name || doc.fileName,
                fileSize: doc.fileSize,
                mimeType: doc.mimeType,
                createdAt: doc.createdAt,
                uploadedByName: doc.uploadedByName ?? null,
                source: "job",
                category,
                jobApiSource: "documents",
            };
        }),
    ];
    const filteredDocuments = combinedRows.filter((row) => {
        if (activeFilter !== "all") {
            if (!categoryMap[activeFilter]?.includes(row.category)) {
                return false;
            }
        }
        if (searchQuery) {
            return row.name.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return true;
    });
    const getFileType = (mimeType) => {
        if (mimeType.includes("pdf"))
            return "PDF";
        if (mimeType.includes("image")) {
            if (mimeType.includes("png"))
                return "PNG";
            if (mimeType.includes("jpeg") || mimeType.includes("jpg"))
                return "JPG";
            if (mimeType.includes("tiff"))
                return "TIFF";
            return "Image";
        }
        return "File";
    };
    if (loading) {
        return _jsx("div", { className: styles.loadingState, children: "Loading documents..." });
    }
    return (_jsxs("div", { className: styles.documentsTab, children: [_jsxs("div", { className: styles.section, children: [_jsxs("div", { className: styles.filterTabs, children: [_jsx("div", { children: filters.map((filter) => (_jsx("button", { className: `${styles.filterTab} ${activeFilter === filter.toLowerCase()
                                        ? styles.filterTabActive
                                        : ""}`, onClick: () => setActiveFilter(filter.toLowerCase()), children: filter }, filter))) }), _jsxs("div", { className: styles.documentsHeader, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsxs("svg", { className: styles.searchIcon, width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value) })] }), _jsxs("button", { type: "button", className: styles.addButton, onClick: () => setIsUploadModalOpen(true), disabled: !job.id, children: [_jsx(UploadIcon, {}), "Add Documents"] })] })] }), _jsx("div", { className: styles.tableWrapper, children: _jsxs("table", { className: styles.documentsTable, children: [_jsx("thead", { children: _jsxs("tr", { className: styles.tableHeader, children: [_jsx("th", { children: "NAME" }), _jsx("th", { children: "DATE" }), _jsx("th", { children: "UPLOADED BY" })] }) }), _jsx("tbody", { children: filteredDocuments.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 3, className: styles.documentsEmptyState, children: _jsxs("div", { className: styles.emptyStateContent, children: [_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("polyline", { points: "14 2 14 8 20 8" })] }), _jsx("p", { children: "No documents found" })] }) }) })) : (filteredDocuments.map((row) => (_jsxs("tr", { className: styles.tableRow, children: [_jsx("td", { children: _jsx("div", { className: styles.documentName, children: _jsxs("div", { className: styles.documentInfo, children: [_jsx("span", { className: styles.documentsName, children: row.name }), _jsxs("span", { className: styles.docMeta, children: [formatFileSize(row.fileSize), " \u2022", " ", getFileType(row.mimeType)] })] }) }) }), _jsx("td", { children: _jsx("span", { className: styles.docDate, children: formatDocDate(row.createdAt) }) }), _jsx("td", { children: _jsxs("div", { className: styles.uploadedBy, children: [_jsxs("div", { className: styles.userInfo, children: [_jsx("div", { className: styles.avatarPlaceholder, children: (row.uploadedByName || "?")
                                                                        .charAt(0)
                                                                        .toUpperCase() }), _jsx("span", { className: styles.userName, children: row.uploadedByName || "Unknown" })] }), _jsx("div", { className: styles.docActions, children: _jsx("button", { className: styles.actionButton, onClick: () => handleDownload(row), title: "Download", children: _jsx(DownloadIcon, {}) }) })] }) })] }, `${row.source}-${row.id}`)))) })] }) }), _jsx("div", { className: styles.pagination, children: _jsxs("span", { children: ["Showing ", filteredDocuments.length, " documents"] }) })] }), _jsx(UploadDocumentsModal, { isOpen: isUploadModalOpen, onClose: () => setIsUploadModalOpen(false), onSave: handleSaveDocuments, categories: JOB_DOCUMENT_CATEGORIES, isUploading: isUploading, uploadButtonText: "Uploading..." })] }));
};
export default ClientJobDetail;
